import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { VehicleAutowareAccessMode } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosError, AxiosResponse } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Autowareアクセスモード変更API
 */
export const useUpdateAutowareAccessModeAPI = (): {
  state: APIState;
  postUpdateAutowareAccessMode: (
    vehicleId: string,
    mode: VehicleAutowareAccessMode,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      mode: VehicleAutowareAccessMode,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/update_autoware_access_mode`,
          {
            mode,
          },
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postUpdateAutowareAccessMode = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, mode: VehicleAutowareAccessMode) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_vehicle_update_autoware_access_mode', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await request(
          project.id,
          environment.environment_id,
          vehicleId,
          mode,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_vehicle_update_autoware_access_mode', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          return false;
        }
        notifySuccess({
          message: t('api.fms.post_vehicle_update_autoware_access_mode', {
            status: 'successfully',
          }),
          testIdAttrs: {
            wrapperTestId: 'autoware_access_mode_snackbar_success-status-div',
            closeButtonTestId:
              'autoware_access_mode_snackbar_success-click-close_button',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    postUpdateAutowareAccessMode,
  };
};
